import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import CommonLink from '@jira/platform__link-with-analytics/src/LinkWithAnalytics.tsx';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import type {
	NameCell$key,
	NameCell$data,
	JiraProjectStyle,
} from '@atlassian/jira-relay/src/__generated__/NameCell.graphql';

type Props = {
	project: NameCell$key;
};

type AvatarSize = '16px' | '24px' | '32px' | '48px' | '64px' | '72px' | '96px' | '128px';

const styles = cssMap({
	avatarStyles: {
		flexShrink: 0,
		width: '24px', // Use of token blocked for width
		height: '24px', // Use of token blocked for height
		borderRadius: token('radius.xsmall'),
		display: 'inline-block',
		verticalAlign: 'middle',
	},
	noAvatarStyles: {
		marginLeft: token('space.400'),
	},
	container: {
		overflow: 'visible',
		whiteSpace: 'normal',
		textOverflow: 'initial',
	},
});

// Get the landing URL directly for JSW projects
export const getProjectLandingUrl = (
	key: string | undefined,
	navigationMetadata: NameCell$data['navigationMetadata'],
	projectStyle: JiraProjectStyle | undefined | null,
): string => {
	if (!navigationMetadata) {
		return `/browse/${key}`;
	}
	if (navigationMetadata.__typename === 'JiraSoftwareProjectNavigationMetadata') {
		if (projectStyle === 'TEAM_MANAGED_PROJECT') {
			return `/jira/software/projects/${key}/boards/${navigationMetadata.boardId}`;
		}

		if (projectStyle === 'COMPANY_MANAGED_PROJECT') {
			return `/jira/software/c/projects/${key}/boards/${navigationMetadata.boardId}`;
		}
	}

	if (navigationMetadata.__typename === 'JiraServiceManagementProjectNavigationMetadata') {
		return `/jira/servicedesk/projects/${key}`;
	}

	return `/browse/${key}`;
};

export const NameCell = ({ project }: Props): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment NameCell on JiraProject {
				projectId
				name
				key
				avatar {
					medium
				}
				projectStyle
				navigationMetadata {
					__typename
					... on JiraSoftwareProjectNavigationMetadata {
						boardId
					}
					... on JiraServiceManagementProjectNavigationMetadata {
						__typename
					}
				}
			}
		`,
		project,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const boardUrl = getProjectLandingUrl(data?.key, data?.navigationMetadata, data?.projectStyle);

	const onLinkClicked = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});

		fireUIAnalytics(analyticsEvent, 'projectDirectoryProjectLink', {
			destProjectId: data.projectId,
		});
	}, [data.projectId, createAnalyticsEvent]);

	if (data?.avatar?.medium) {
		return (
			<Box xcss={styles.container}>
				<Tooltip
					content={data.name}
					isScreenReaderAnnouncementDisabled
					delay={1000}
					position="bottom-start"
				>
					<Link href={boardUrl} onClick={onLinkClicked}>
						<Box xcss={styles.avatarStyles} as="img" src={data.avatar.medium} alt="" />
						<WithAvatarLabel>{data.name}</WithAvatarLabel>
					</Link>
				</Tooltip>
			</Box>
		);
	}
	return (
		<Box xcss={styles.container}>
			<Tooltip
				content={data.name}
				isScreenReaderAnnouncementDisabled
				delay={1000}
				position="bottom-start"
			>
				<Link href={boardUrl}>
					<Box as="span" xcss={styles.noAvatarStyles}>
						{data.name}
					</Box>
				</Link>
			</Tooltip>
		</Box>
	);
};

export const avatarSizePx: AvatarSize = '24px';

// When we need to display a label for a project that has an avatar, we need to just ensure that there is a gap
// between the avatar and the label. This is why we set the left margin to 8px / space.100.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WithAvatarLabel = styled.span({
	marginLeft: token('space.100'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled(CommonLink)({
	display: 'block',
	overflowX: 'hidden',
});
